import type { IUserRepository, PaginationParams, SearchParams } from "@src/domain/users/repository/user.repository.interface";
import type { UserDTO, PaginatedUsersDTO } from "../dto/user.dto";
import type { CreateUserInputDTO, UpdateUserInputDTO, UpdateProfileInputDTO } from "../dto/user-input.dto";
import { CreateUserUseCase } from "./create_user.usecase";
import { ListAllUsersUseCase } from "./list_all_users.usecase";
import { SearchUsersUseCase } from "./search_users.usecase";
import { GetUserByIdUseCase } from "./get_user_by_id.usecase";
import { UpdateUserUseCase } from "./update_user.usecase";
import { BanUserUseCase } from "./ban_user.usecase";
import { UnbanUserUseCase } from "./unban_user.usecase";
import { UpdateProfileUseCase } from "./update_profile.usecase";
import { UploadProfilePictureUseCase } from "./upload_profile_picture.usecase";

/**
 * User Service
 * Aggregates all user-related use cases for easier access
 */
export class UserService {
    private readonly createUserUseCase: CreateUserUseCase;
    private readonly listAllUsersUseCase: ListAllUsersUseCase;
    private readonly searchUsersUseCase: SearchUsersUseCase;
    private readonly getUserByIdUseCase: GetUserByIdUseCase;
    private readonly updateUserUseCase: UpdateUserUseCase;
    private readonly banUserUseCase: BanUserUseCase;
    private readonly unbanUserUseCase: UnbanUserUseCase;
    private readonly updateProfileUseCase: UpdateProfileUseCase;
    private readonly uploadProfilePictureUseCase: UploadProfilePictureUseCase;

    constructor(userRepository: IUserRepository) {
        this.createUserUseCase = new CreateUserUseCase(userRepository);
        this.listAllUsersUseCase = new ListAllUsersUseCase(userRepository);
        this.searchUsersUseCase = new SearchUsersUseCase(userRepository);
        this.getUserByIdUseCase = new GetUserByIdUseCase(userRepository);
        this.updateUserUseCase = new UpdateUserUseCase(userRepository);
        this.banUserUseCase = new BanUserUseCase(userRepository);
        this.unbanUserUseCase = new UnbanUserUseCase(userRepository);
        this.updateProfileUseCase = new UpdateProfileUseCase(userRepository);
        this.uploadProfilePictureUseCase = new UploadProfilePictureUseCase(userRepository);
    }

    /**
     * Create a new user with specific roles (Admin only)
     */
    async create(data: CreateUserInputDTO): Promise<UserDTO> {
        return this.createUserUseCase.execute(data);
    }

    /**
     * List all users with pagination (Admin only)
     */
    async listAll(params?: PaginationParams): Promise<PaginatedUsersDTO> {
        return this.listAllUsersUseCase.execute(params);
    }

    /**
     * Search users with filters and pagination (Admin only)
     */
    async search(params?: SearchParams): Promise<PaginatedUsersDTO> {
        return this.searchUsersUseCase.execute(params);
    }

    /**
     * Get a specific user by ID (Admin only)
     */
    async getById(userId: string): Promise<UserDTO | null> {
        return this.getUserByIdUseCase.execute(userId);
    }

    /**
     * Update user information (Admin only)
     */
    async update(userId: string, data: UpdateUserInputDTO): Promise<UserDTO> {
        return this.updateUserUseCase.execute(userId, data);
    }

    /**
     * Ban a user from accessing the system (Admin only)
     */
    async ban(userId: string): Promise<UserDTO> {
        return this.banUserUseCase.execute(userId);
    }

    /**
     * Unban a user (Admin only)
     */
    async unban(userId: string): Promise<UserDTO> {
        return this.unbanUserUseCase.execute(userId);
    }

    /**
     * Update authenticated user's profile
     */
    async updateProfile(data: UpdateProfileInputDTO): Promise<UserDTO> {
        return this.updateProfileUseCase.execute(data);
    }

    /**
     * Upload profile picture for authenticated user
     */
    async uploadProfilePicture(file: File, onUploadProgress?: (progress: number) => void): Promise<UserDTO> {
        return this.uploadProfilePictureUseCase.execute(file, onUploadProgress);
    }
}
